import BaseGrid from 'components/atoms/base-grid';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { combineFormResults } from 'infrastructure/functions/combine-form-results';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { scrollToErrorIfExist } from 'utils';

import ClinicContactInformationForm from './contact-information';
import ClinicAddressForm from './address';
import ClinicDetailForm from './detail';
import ClinicInstructionsForm from './instructions';
import ClinicRepresentativesForm from './representatives';
import ClinicMappingsForm from './mappings';
import ClinicThresholdsForm from './thresholds';
import ClinicSettingsForm from './settings';
import ClinicCareManagersForm from './care-managers';

import type { ClinicThresholds } from 'infrastructure/classes/clinic-thresholds';
import type { Clinic } from 'infrastructure/classes/clinic';
import type {
  IClinic,
  IClinicMappings,
  IClinicThreshold,
} from 'infrastructure/interfaces';
import type {
  IIndependentFormProps,
  IIndependentFormRef,
} from 'infrastructure/interfaces/common/i-independent-form';

const FormDisplayName = 'ClinicForm';

export interface IClinicFormRef
  extends IIndependentFormRef<IClinic & IClinicThreshold> {}

interface IProps extends IIndependentFormProps {
  info?: Clinic;
  thresholds?: ClinicThresholds;
  loading?: boolean;
  isEditMode?: boolean;
}

const ClinicForm = forwardRef<IClinicFormRef, IProps>((props, ref) => {
  const { info, thresholds, loading = false, isEditMode } = props;

  const hasInfo = Boolean(info);

  const settingsRef = useRef<IIndependentFormRef>(null);
  const contactInfoRef = useRef<IIndependentFormRef>(null);
  const contactAddressRef = useRef<IIndependentFormRef>(null);
  const clinicDetailRef = useRef<IIndependentFormRef>(null);
  const clinicInstructionsRef = useRef<IIndependentFormRef>(null);
  const clinicMappingsFormRef = useRef<IIndependentFormRef>(null);
  const clinicRepresentativesFormRef = useRef<IIndependentFormRef>(null);
  const clinicThresholdsFormRef = useRef<IIndependentFormRef>(null);
  const clinicCareManagersFormRef = useRef<IIndependentFormRef>(null);

  const [clinicMappings, setClinicMappings] = useState<
    Partial<IClinicMappings>
  >({
    agencyGuid: undefined,
    parentClinicGuid: null,
  });

  const changeMappings = (next: Partial<IClinicMappings>) => {
    setClinicMappings((prev) => {
      return {
        ...prev,
        ...next,
      };
    });
  };

  const submitForm = async () => {
    const res = await combineFormResults<IClinic & IClinicThreshold>(
      [
        settingsRef,
        contactInfoRef,
        contactAddressRef,
        clinicDetailRef,
        clinicInstructionsRef,
        clinicMappingsFormRef,
        clinicRepresentativesFormRef,
        clinicThresholdsFormRef,
        clinicCareManagersFormRef,
      ],
      hasInfo,
    )
      .then((values) => {
        return {
          values,
          formName: FormDisplayName,
          isValid: true,
        };
      })
      .catch((err) => {
        scrollToErrorIfExist();
        return err;
      });
    return res;
  };

  const resetForm = () => {
    settingsRef.current?.reset();
    contactInfoRef.current?.reset();
    contactAddressRef.current?.reset();
    clinicDetailRef.current?.reset();
    clinicInstructionsRef.current?.reset();
    clinicMappingsFormRef.current?.reset();
    clinicRepresentativesFormRef.current?.reset();
    clinicThresholdsFormRef.current?.reset();
    clinicCareManagersFormRef.current?.reset();
  };

  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: resetForm,
  }));

  return (
    <BaseSpinWrapper spinning={loading}>
      <BaseGrid columns={1} rowGap={40}>
        <ClinicSettingsForm ref={settingsRef} data={info} />
        <BaseGrid columns={2} columnGap={20}>
          <ClinicContactInformationForm ref={contactInfoRef} data={info} />
          <ClinicAddressForm ref={contactAddressRef} data={info} />
        </BaseGrid>
        <ClinicDetailForm ref={clinicDetailRef} data={info} />
        <ClinicInstructionsForm
          ref={clinicInstructionsRef}
          clinicMappings={clinicMappings}
          data={info}
        />
        <BaseGrid columns={2} columnGap={20}>
          <ClinicMappingsForm
            onChange={changeMappings}
            ref={clinicMappingsFormRef}
            data={info}
          />
          <ClinicRepresentativesForm
            ref={clinicRepresentativesFormRef}
            data={info}
          />
        </BaseGrid>
        <ClinicCareManagersForm
          isEditMode={isEditMode}
          agencyGuid={clinicMappings.agencyGuid}
          ref={clinicCareManagersFormRef}
          data={info}
        />
        <ClinicThresholdsForm ref={clinicThresholdsFormRef} data={thresholds} />
      </BaseGrid>
    </BaseSpinWrapper>
  );
});

ClinicForm.displayName = FormDisplayName;

export default ClinicForm;
